import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FileText, Loader2, ExternalLink, Download, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useDocuments, getDocumentSignedUrl } from '@/features/documents/useDocuments';
import type { ClinanceDocument } from '@/features/documents/types';

function bytesToReadable(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(2)} MB`;
}

function ReportCard({ doc }: { doc: ClinanceDocument }) {
  const [busy, setBusy] = useState(false);
  const sanitizedName = doc.file_path.split('/').pop()?.replace(/^\d+_/, '') ?? doc.name;

  const openInNewTab = async () => {
    setBusy(true);
    const url = await getDocumentSignedUrl(doc.file_path);
    setBusy(false);
    if (!url) {
      toast.error('Não foi possível gerar o link.');
      return;
    }
    window.open(url, '_blank', 'noreferrer');
  };

  const downloadFile = async () => {
    setBusy(true);
    try {
      const url = await getDocumentSignedUrl(doc.file_path);
      if (!url) throw new Error('Sem URL.');
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = sanitizedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(`Erro ao baixar: ${e instanceof Error ? e.message : 'desconhecido'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="rounded-lg bg-indigo-100 p-2.5 text-indigo-700">
          <FileText size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground">{doc.name}</h3>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {doc.document_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarIcon size={11} />
                {format(new Date(`${doc.document_date}T00:00:00`), 'dd/MM/yyyy', {
                  locale: ptBR,
                })}
              </span>
            )}
            <span>{bytesToReadable(doc.size_bytes)}</span>
          </div>
          {doc.notes && (
            <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{doc.notes}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openInNewTab}
              disabled={busy}
              className="h-7 text-xs"
            >
              {busy ? (
                <Loader2 size={11} className="mr-1.5 animate-spin" />
              ) : (
                <ExternalLink size={11} className="mr-1.5" />
              )}
              Visualizar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadFile}
              disabled={busy}
              className="h-7 text-xs"
            >
              <Download size={11} className="mr-1.5" />
              Baixar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PortalReportsPage() {
  const { client, clientId } = useCurrentClient();
  const { data: docs = [], isLoading } = useDocuments({ type: 'report' });

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Sem cliente selecionado.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">Relatórios</h1>
        <p className="text-sm text-muted-foreground">
          {client?.name ? `${client.name} · ` : ''}relatórios financeiros disponibilizados pela
          consultoria
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : docs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm font-medium text-foreground">Nenhum relatório ainda.</p>
            <p className="mt-1 text-xs">
              A consultoria gera relatórios mensais automaticamente — eles aparecerão aqui assim que
              estiverem disponíveis.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {docs.map((doc) => (
            <ReportCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}
